import React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Database,
  Calendar,
  Clock,
  CheckCircle2,
  Loader2,
  TrendingUp,
  History,
} from "lucide-react";
import {
  subscribeSyncDetails,
  getGlobalSyncDetails,
  type SyncDetails,
} from "@/lib/sync-status-bus";
import { cn } from "@/lib/utils";

// Icons para os grupos de fonte
const GoogleIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

const MetaIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 80 80" fill="none">
    <path
      d="M23.5 68c-6.5 0-11.2-2.8-14.3-6.9-2.9-3.9-4.3-9.2-4.3-14.6 0-7.5 2.6-15.8 7.6-22.3C17.6 17.5 25 12 34 12c5.6 0 10 2.2 13.6 6.2 3.5-4 8-6.2 13.6-6.2 9 0 16.4 5.5 21.5 12.2 5 6.5 7.6 14.8 7.6 22.3 0 5.4-1.4 10.7-4.3 14.6-3.1 4.1-7.8 6.9-14.3 6.9-7.4 0-13-4.4-16.7-9.5l-5 7c-2.2 3.1-5.7 5-9.4 5s-7.2-1.9-9.4-5l-5-7c-3.7 5.1-9.3 9.5-16.7 9.5zm2.4-12.7c2.7 3.5 5.8 5.2 9.1 5.2 2.4 0 4.5-1.3 5.8-3.1l7.4-10.4c1.3-1.8 3.4-2.9 5.6-2.9h.4c2.2 0 4.3 1.1 5.6 2.9l7.4 10.4c1.3 1.8 3.4 3.1 5.8 3.1 3.3 0 6.4-1.7 9.1-5.2 2.5-3.2 3.8-7.7 3.8-12.8 0-6.5-2.3-13.5-6.5-18.9-4.1-5.3-9.7-8.6-16.2-8.6-4.2 0-7.8 1.8-10.7 5.3l-3.6 4.5c-1.3 1.6-3.3 2.6-5.4 2.6s-4.1-1-5.4-2.6l-3.6-4.5c-2.9-3.5-6.5-5.3-10.7-5.3-6.5 0-12.1 3.3-16.2 8.6-4.2 5.4-6.5 12.4-6.5 18.9 0 5.1 1.3 9.6 3.8 12.8z"
      fill="#0081FB"
    />
  </svg>
);

const phaseLabels: Record<string, string> = {
  initial_7: "Últimos 7 dias",
  initial_30: "Últimos 30 dias",
  initial_90: "Últimos 90 dias",
  initial_180: "Últimos 180 dias",
  historical_backfill: "Histórico completo",
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "-";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "-";
  }
};

const BackfillProgressCard: React.FC = () => {
  const [details, setDetails] =
    React.useState<SyncDetails>(getGlobalSyncDetails);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    // Marcar como carregado após primeira atualização de detalhes
    const timer = setTimeout(() => setIsLoading(false), 2000);

    const unsubscribe = subscribeSyncDetails((newDetails) => {
      setDetails(newDetails);
      setIsLoading(false);
      if (newDetails.isBackfilling) {
        setIsAnimating(true);
      } else {
        setIsAnimating(false);
      }
    });

    return () => {
      clearTimeout(timer);
      unsubscribe();
    };
  }, []);

  const hasData =
    details.dataPeriod?.start_date || details.dataPeriod?.end_date;
  const isBackfilling = details.isBackfilling;
  const isInitialSyncing = details.isSyncing && details.isInitialSync;

  const progress = details.backfillProgress;
  const periodBySource = details.dataPeriodBySource;
  const jobsBySource = details.jobsBySource;

  // Verificar se há dados por fonte
  const hasGoogleData =
    periodBySource?.google?.start_date || periodBySource?.google?.end_date;
  const hasMetaData =
    periodBySource?.meta?.start_date || periodBySource?.meta?.end_date;

  // Verificar se há jobs por fonte (indica que está conectado e processando)
  const googleJobs = jobsBySource?.google;
  const metaJobs = jobsBySource?.meta;

  // Jobs pendentes por fonte
  const googlePendingJobs = googleJobs?.pending ?? 0;
  const metaPendingJobs = metaJobs?.pending ?? 0;

  // Sincronização ativa por fonte (evita exibir "0 jobs" quando não há pendências)
  const isGoogleSyncing =
    googlePendingJobs > 0 || (details.isSyncing && details.source === "google");
  const isMetaSyncing =
    metaPendingJobs > 0 || (details.isSyncing && details.source === "meta");

  // Calcular progresso visual baseado nos jobs concluídos
  const progressPercent =
    progress?.phase_progress_percent ?? (hasData && !isBackfilling ? 100 : 0);
  const jobsCompleted = progress?.jobs_completed ?? 0;
  const jobsPending = progress?.jobs_pending ?? 0;
  const totalJobs = progress?.total_jobs ?? 0;

  // Determinar estado atual para exibição
  const getStatusInfo = () => {
    if (isLoading) {
      return {
        badge: (
          <Badge
            variant="outline"
            className="bg-muted/50 text-muted-foreground border-border"
          >
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Carregando
          </Badge>
        ),
        message: "Verificando status da sincronização...",
        icon: (
          <Loader2 className="h-6 w-6 text-muted-foreground animate-spin" />
        ),
      };
    }
    if (isInitialSyncing) {
      return {
        badge: (
          <Badge
            variant="outline"
            className="bg-yellow-500/10 text-yellow-500 border-yellow-500/30"
          >
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Sincronização Inicial
          </Badge>
        ),
        message: "Sincronização inicial em andamento. Aguarde a conclusão...",
        icon: <Loader2 className="h-6 w-6 text-yellow-500 animate-spin" />,
      };
    }
    if (isBackfilling) {
      return {
        badge: (
          <Badge
            variant="outline"
            className="bg-blue-500/10 text-blue-500 border-blue-500/30"
          >
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Sincronizando Histórico
          </Badge>
        ),
        message: "Estamos sincronizando o restante de seus dados históricos...",
        icon: <Loader2 className="h-6 w-6 text-primary animate-spin" />,
      };
    }
    if (hasData) {
      return {
        badge: (
          <Badge
            variant="outline"
            className="bg-green-500/10 text-green-500 border-green-500/30"
          >
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Sincronizado
          </Badge>
        ),
        message: "Seus dados estão sincronizados e prontos para análise.",
        icon: <Database className="h-6 w-6 text-primary" />,
      };
    }
    return {
      badge: (
        <Badge
          variant="outline"
          className="bg-muted/50 text-muted-foreground border-border"
        >
          Aguardando
        </Badge>
      ),
      message: "Conecte uma conta para iniciar a sincronização de dados.",
      icon: <Database className="h-6 w-6 text-muted-foreground" />,
    };
  };

  const statusInfo = getStatusInfo();

  return (
    <Card className="col-span-12 overflow-hidden">
      {/* Header com gradiente */}
      <div
        className={cn(
          "relative p-6 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-b border-border/50",
          isAnimating && "animate-pulse",
        )}
      >
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-40 h-40 bg-primary rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-500 rounded-full blur-3xl" />
        </div>

        <div className="relative flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div
              className={cn(
                "p-3 rounded-xl bg-primary/10 border border-primary/20",
                (isBackfilling || isInitialSyncing) && "animate-pulse",
              )}
            >
              {statusInfo.icon}
            </div>
            <div>
              <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                Dados Sincronizados
                {statusInfo.badge}
              </h3>
              <p className="text-muted-foreground text-sm mt-1">
                {statusInfo.message}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Período de dados por fonte */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Calendar className="h-4 w-4" />
              Período de Dados por Fonte
            </div>
            <div className="space-y-3">
              {/* Google */}
              <div className="bg-muted/30 rounded-lg p-3 border border-border/50">
                <div className="flex items-center gap-2 mb-2">
                  <GoogleIcon />
                  <span className="text-xs font-semibold text-foreground">
                    GOOGLE
                  </span>
                  <span className="text-xs text-muted-foreground">
                    (Analytics, Ads, Search Console)
                  </span>
                </div>
                {hasGoogleData ? (
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-foreground">
                      {formatDate(periodBySource?.google?.start_date)}
                    </span>
                    <div className="flex-1 mx-2 h-px bg-gradient-to-r from-blue-500/50 via-blue-500 to-blue-500/50" />
                    <span className="font-medium text-foreground">
                      {formatDate(periodBySource?.google?.end_date)}
                    </span>
                    {periodBySource?.google?.total_days && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        ({periodBySource.google.total_days}d)
                      </span>
                    )}
                    {googlePendingJobs > 0 && (
                      <span className="ml-2 text-xs text-blue-500 flex items-center gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" />+
                        {googlePendingJobs} jobs
                      </span>
                    )}
                  </div>
                ) : isGoogleSyncing ? (
                  <div className="flex items-center gap-2 text-xs text-blue-500">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>
                      {googlePendingJobs > 0
                        ? `Sincronizando... (${googlePendingJobs} jobs na fila)`
                        : "Sincronizando..."}
                    </span>
                  </div>
                ) : isLoading ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>Carregando...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>Sem dados sincronizados</span>
                  </div>
                )}
              </div>

              {/* Meta */}
              <div className="bg-muted/30 rounded-lg p-3 border border-border/50">
                <div className="flex items-center gap-2 mb-2">
                  <MetaIcon />
                  <span className="text-xs font-semibold text-foreground">
                    META
                  </span>
                  <span className="text-xs text-muted-foreground">
                    (Ads, Instagram)
                  </span>
                </div>
                {hasMetaData ? (
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-foreground">
                      {formatDate(periodBySource?.meta?.start_date)}
                    </span>
                    <div className="flex-1 mx-2 h-px bg-gradient-to-r from-blue-600/50 via-blue-600 to-blue-600/50" />
                    <span className="font-medium text-foreground">
                      {formatDate(periodBySource?.meta?.end_date)}
                    </span>
                    {periodBySource?.meta?.total_days && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        ({periodBySource.meta.total_days}d)
                      </span>
                    )}
                    {metaPendingJobs > 0 && (
                      <span className="ml-2 text-xs text-blue-500 flex items-center gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" />+
                        {metaPendingJobs} jobs
                      </span>
                    )}
                  </div>
                ) : isMetaSyncing ? (
                  <div className="flex items-center gap-2 text-xs text-blue-500">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>
                      {metaPendingJobs > 0
                        ? `Sincronizando... (${metaPendingJobs} jobs na fila)`
                        : "Sincronizando..."}
                    </span>
                  </div>
                ) : isLoading ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>Carregando...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>Sem dados sincronizados</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Status do Backfill */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <History className="h-4 w-4" />
              Progresso da Sincronização
            </div>
            <div className="bg-muted/30 rounded-lg p-4 border border-border/50">
              {isInitialSyncing ? (
                <div className="flex flex-col items-center justify-center py-2">
                  <Loader2 className="h-8 w-8 text-yellow-500 animate-spin mb-2" />
                  <span className="text-sm font-medium text-foreground">
                    Sincronização inicial
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Coletando primeiros dados...
                  </span>
                  {totalJobs > 0 && (
                    <span className="text-xs text-muted-foreground mt-1">
                      {jobsPending} jobs restantes
                    </span>
                  )}
                </div>
              ) : isBackfilling && progress ? (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">
                      Fase:{" "}
                      {progress.current_phase
                        ? phaseLabels[progress.current_phase] ||
                          progress.current_phase
                        : "-"}
                    </span>
                    <span className="text-sm font-medium text-foreground">
                      {jobsCompleted}/{totalJobs} jobs
                    </span>
                  </div>
                  <Progress value={progressPercent} className="h-2 mb-2" />
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{progressPercent}% concluído</span>
                    <span className="text-blue-500 font-medium">
                      {jobsPending} restantes
                    </span>
                  </div>
                </>
              ) : hasData && !isBackfilling ? (
                <div className="flex flex-col items-center justify-center py-2">
                  <CheckCircle2 className="h-8 w-8 text-green-500 mb-2" />
                  <span className="text-sm font-medium text-foreground">
                    Sincronização completa
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Todos os dados disponíveis
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-2">
                  <History className="h-8 w-8 text-muted-foreground/50 mb-2" />
                  <span className="text-sm text-muted-foreground">
                    Aguardando sincronização
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Informação adicional */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <TrendingUp className="h-4 w-4" />
              Informações
            </div>
            <div className="bg-gradient-to-br from-blue-500/5 to-primary/5 rounded-lg p-4 border border-blue-500/20">
              <p className="text-sm text-foreground leading-relaxed">
                {isInitialSyncing ? (
                  <>
                    <strong className="text-yellow-500">
                      Sincronização inicial
                    </strong>{" "}
                    em andamento. Estamos coletando os primeiros dados das suas
                    contas conectadas. Aguarde a conclusão para acessar o
                    painel.
                  </>
                ) : isBackfilling ? (
                  <>
                    <strong className="text-primary">Em breve</strong> você terá
                    no Painel do E-Commerce todo o histórico de suas contas
                    conectadas. A sincronização está rodando em segundo plano.
                  </>
                ) : hasData ? (
                  <>
                    <strong className="text-green-500">Pronto!</strong> Todos os
                    seus dados históricos estão disponíveis para análise.
                    Explore suas métricas e insights no painel.
                  </>
                ) : (
                  <>
                    <strong className="text-muted-foreground">
                      Aguardando
                    </strong>{" "}
                    sincronização de dados. Assim que suas contas forem
                    conectadas, iniciaremos a coleta automática.
                  </>
                )}
              </p>
              {isBackfilling && !isInitialSyncing && (
                <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  <span>Você pode continuar usando o sistema normalmente</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default BackfillProgressCard;
